import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Landmark, RefreshCcw, AlertCircle, Upload, Clock, ChevronRight, Wifi,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

function formatCHF(v, currency = "CHF") {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("de-CH", { style: "currency", currency, minimumFractionDigits: 2 }).format(v);
}

function timeAgo(iso) {
  if (!iso) return "noch nie";
  try {
    const date = new Date(iso);
    const diff = Date.now() - date.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "gerade eben";
    if (m < 60) return `vor ${m} Min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `vor ${h} Std`;
    const d = Math.floor(h / 24);
    if (d < 30) return `vor ${d} ${d === 1 ? "Tag" : "Tagen"}`;
    return date.toLocaleDateString("de-CH", { year: "numeric", month: "short", day: "2-digit" });
  } catch { return "—"; }
}

function maskIban(iban) {
  if (!iban) return null;
  const clean = String(iban).replace(/\s/g, "");
  if (clean.length < 8) return clean;
  return `${clean.slice(0, 4)} … ${clean.slice(-4)}`;
}

/**
 * Iter83: Bank-Cockpit-Card
 * Zeigt pro Bankkonto: Saldo, IBAN, letzter Sync, ungematschte Bewegungen.
 * Auf Klick auf eine Zeile springt man zur Bank-Reconciliation.
 */
export default function BankCockpitCard({ companyId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/bank-cockpit/summary`);
      setData(r.data);
    } catch (e) {
      // Stille degradation — Card wird einfach nicht angezeigt
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !data) {
    return (
      <Card className="p-5 border-slate-200" data-testid="bank-cockpit-loading">
        <p className="text-sm text-slate-500">Lade Bank-Übersicht …</p>
      </Card>
    );
  }
  if (!data || !data.accounts) return null;

  const hasAccounts = data.accounts.length > 0;
  const totalUnmatched = data.total_unmatched || 0;
  const noSyncYet = !data.last_sync_at;

  return (
    <Card
      className="p-5 border border-indigo-200 bg-gradient-to-br from-indigo-50/40 via-white to-indigo-50/20"
      data-testid="bank-cockpit-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center shadow-sm">
            <Landmark className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">Bank-Cockpit</h3>
            <p className="text-xs text-slate-500">
              {data.accounts.length} {data.accounts.length === 1 ? "Konto" : "Konten"} ·
              Total {formatCHF(data.total_balance, data.currency)} ·
              Letzter Sync: <b>{timeAgo(data.last_sync_at)}</b>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {totalUnmatched > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs" data-testid="bank-cockpit-unmatched-badge">
              <AlertCircle className="h-3 w-3 mr-1" />
              {totalUnmatched} offene Buchung{totalUnmatched !== 1 ? "en" : ""}
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={refresh} title="Aktualisieren" data-testid="bank-cockpit-refresh">
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {!hasAccounts ? (
        <div className="text-center py-6 text-sm text-slate-500">
          Keine Bankkonten konfiguriert. Lege ein Konto (z.B. 1020) im Kontenplan an.
        </div>
      ) : (
        <div className="space-y-2" data-testid="bank-cockpit-accounts-list">
          {data.accounts.map((acc) => (
            <button
              key={acc.account_id}
              type="button"
              onClick={() => navigate("/bank-reconciliation")}
              className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
              data-testid={`bank-cockpit-account-${acc.account_id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                  <Landmark className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] text-slate-400">{acc.account_id}</span>
                    <span className="text-sm font-medium text-slate-900 truncate">{acc.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                    {acc.iban && (
                      <span className="font-mono">{maskIban(acc.iban)}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {timeAgo(acc.last_sync_at)}
                    </span>
                    {acc.unmatched_count > 0 && (
                      <span className="text-amber-700 font-medium">
                        · {acc.unmatched_count} offen
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-right font-semibold tabular-nums ${acc.balance < 0 ? "text-rose-700" : "text-slate-900"}`}>
                  {formatCHF(acc.balance, acc.currency)}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate("/bank-reconciliation")}
          data-testid="bank-cockpit-go-reconciliation"
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          camt.053 hochladen
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate("/settings#section-postfinance")}
          data-testid="bank-cockpit-postfinance"
        >
          <Wifi className="h-3.5 w-3.5 mr-1.5" />
          PostFinance verbinden
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate("/settings#section-ebics")}
        >
          <Wifi className="h-3.5 w-3.5 mr-1.5" />
          EBICS einrichten
        </Button>
        {totalUnmatched > 0 && (
          <Button
            size="sm"
            onClick={() => navigate("/bank-reconciliation")}
            className="ml-auto bg-amber-600 hover:bg-amber-700"
            data-testid="bank-cockpit-resolve-cta"
          >
            {totalUnmatched} Buchung{totalUnmatched !== 1 ? "en" : ""} zuordnen →
          </Button>
        )}
        {noSyncYet && totalUnmatched === 0 && (
          <span className="ml-auto text-[11px] text-slate-500 italic">
            Tipp: Zuerst camt.053 hochladen oder PostFinance/EBICS verbinden
          </span>
        )}
      </div>
    </Card>
  );
}
